// The public catalogue from accounts (GET /v1/catalogue) and the pure browse logic: Kind → genre → facets, plus
// search. Tech tags are developer-view only, so they never appear here (docs/PLAN.md, catalogue decision).

import { api, ApiError } from "./api.ts";

export type Kind = "game" | "experience" | "learn" | "example";
export type Input = "keyboard" | "mouse" | "touch" | "gamepad";

export interface Creator {
  id: string;
  name: string;
  url: string | null;
  claimedBy: string | null;
}

export interface Game {
  id: string;
  slug: string;
  kind: Kind;
  genre: string;
  title: string;
  url: string;
  runtime: "web" | "native";
  creator: Creator;
  blurb: string;
  note: string | null;
  art: string | null;
  palette: { top: string; bottom: string };
  facets: { multiplayer: boolean; input: Input[]; prePlay: string[] };
  updatedAt: number;
  /** Threedom's own game (the plaza); accounts wave 4 */
  firstParty?: boolean;
  /** reviewed local-network opt-in, exact origins; accounts wave 4 */
  localNetwork?: string[];
  /** accounts wave 6: a game can be listed before it's out (a waitlist); missing means released */
  status?: "released" | "coming_soon";
  /** expected release, unix seconds, while coming soon */
  releaseAt?: number | null;
  /** when it was released, unix seconds */
  releasedAt?: number | null;
}

export interface List {
  id: string;
  title: string;
  style: "hero" | "spotlight" | "row" | string;
  games: string[];
}

export interface Catalogue {
  version: string;
  updatedAt: number;
  taxonomy: { kinds: Kind[]; genres: Record<string, string[]>; facets: Record<string, unknown> };
  games: Game[];
  creators: Creator[];
  lists: List[];
}

let cached: Promise<Catalogue> | null = null;

export function getCatalogue(): Promise<Catalogue> {
  cached ??= api<Catalogue>("accounts", "/catalogue", { auth: false }).catch((e) => {
    cached = null;
    throw e;
  });
  return cached;
}

/**
 * A creator by id (`cr_…`). The catalogue's list holds only creators with a listed game, so a creator with just a
 * handle (accounts wave 6, `POST /v1/creators`) comes from accounts' single read `GET /v1/catalogue/creators/:id`.
 * `games` are their listed games' ids. null = no such creator.
 */
export async function findCreator(id: string): Promise<{ creator: Creator; games: string[] } | null> {
  const cat = await getCatalogue().catch(() => null);
  const listed = cat?.creators.find((c) => c.id === id);
  if (listed) return { creator: listed, games: cat!.games.filter((g) => g.creator.id === id).map((g) => g.id) };
  if (!/^cr_[a-z0-9][a-z0-9_-]{0,63}$/.test(id)) return null;
  let p = single.get(id);
  if (!p) single.set(id, (p = readCreator(id)));
  return p.catch((e) => {
    single.delete(id);
    throw e;
  });
}

/** Single creator reads, once per page load (a creator page and its assets section both ask). */
const single = new Map<string, Promise<{ creator: Creator; games: string[] } | null>>();

async function readCreator(id: string): Promise<{ creator: Creator; games: string[] } | null> {
  try {
    const r = await api<{ creator?: Partial<Creator>; games?: unknown }>("accounts", `/catalogue/creators/${encodeURIComponent(id)}`, { auth: false });
    const c = r.creator;
    if (!c || typeof c.id !== "string") return null;
    return {
      creator: { id: c.id, name: typeof c.name === "string" && c.name ? c.name : c.id.replace(/^cr_/, ""), url: typeof c.url === "string" ? c.url : null, claimedBy: typeof c.claimedBy === "string" ? c.claimedBy : null },
      games: Array.isArray(r.games) ? r.games.filter((g): g is string => typeof g === "string") : [],
    };
  } catch (e) {
    if (e instanceof ApiError && e.status === 404) return null; // accounts' "no such creator" (and an old accounts without the read)
    throw e;
  }
}

/** Forget the cached catalogue (after a claim changes `claimedBy`). */
export function refreshCatalogue(): void {
  cached = null;
  single.clear();
}

export const KIND_LABEL: Record<Kind, string> = { game: "Games", experience: "Experiences", learn: "Learn", example: "Examples" };
export const INPUT_LABEL: Record<Input, string> = { keyboard: "Keyboard", mouse: "Mouse", touch: "Touch", gamepad: "Gamepad" };
export const PREPLAY_LABEL: Record<string, string> = {
  ads: "Ads before play",
  cookie_banner: "Cookie banner",
  consent_screen: "Consent screen",
  bot_check: "Bot check",
  long_load: "Long first load",
};

export function label(slug: string): string {
  return slug.charAt(0).toUpperCase() + slug.slice(1).replace(/[-_]/g, " ");
}

export interface Filter {
  kind?: Kind | null;
  genre?: string | null;
  multiplayer?: boolean;
  input?: Input[];
  /** only games that open straight into play (no ads, banners, bot checks…) */
  noPrePlay?: boolean;
  q?: string;
}

/** Search tokens; diacritics and case don't matter. */
export function tokens(s: string): string[] {
  return s
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .split(/[^a-z0-9]+/)
    .filter(Boolean);
}

/** 0 = no match. Title hits outrank creator and genre hits, which outrank the blurb. Every query word must match. */
export function searchScore(g: Game, q: string): number {
  const words = tokens(q);
  if (words.length === 0) return 1;
  const title = tokens(g.title);
  const meta = tokens(`${g.creator.name} ${g.genre} ${g.kind}`);
  const blurb = tokens(g.blurb);
  let score = 0;
  for (const w of words) {
    const hit = (list: string[]) => list.some((t) => t.startsWith(w));
    if (title.some((t) => t === w)) score += 10;
    else if (hit(title)) score += 6;
    else if (hit(meta)) score += 3;
    else if (hit(blurb)) score += 1;
    else return 0;
  }
  return score;
}

export function filterGames(games: Game[], f: Filter): Game[] {
  const out = games.filter(
    (g) =>
      (!f.kind || g.kind === f.kind) &&
      (!f.genre || g.genre === f.genre) &&
      (!f.multiplayer || g.facets.multiplayer) &&
      (!f.input?.length || f.input.every((i) => g.facets.input.includes(i))) &&
      (!f.noPrePlay || g.facets.prePlay.length === 0),
  );
  if (!f.q?.trim()) return out;
  return out
    .map((g, i) => ({ g, i, s: searchScore(g, f.q!) }))
    .filter((x) => x.s > 0)
    .sort((a, b) => b.s - a.s || a.i - b.i)
    .map((x) => x.g);
}

/** Genres that have at least one game of this kind, in taxonomy order. */
export function genresFor(cat: Pick<Catalogue, "taxonomy" | "games">, kind: Kind | null | undefined): string[] {
  const present = new Set(cat.games.filter((g) => !kind || g.kind === kind).map((g) => g.genre));
  const order = kind ? (cat.taxonomy.genres[kind] ?? []) : Object.values(cat.taxonomy.genres).flat();
  const seen = new Set<string>();
  const out: string[] = [];
  for (const g of [...order, ...present]) if (present.has(g) && !seen.has(g)) (seen.add(g), out.push(g));
  return out;
}

export function filterFromQuery(params: URLSearchParams): Filter {
  const kind = params.get("kind") as Kind | null;
  return {
    kind: kind && ["game", "experience", "learn", "example"].includes(kind) ? kind : null,
    genre: params.get("genre"),
    multiplayer: params.get("multiplayer") === "1",
    input: (params.get("input") ?? "").split(",").filter((i): i is Input => ["keyboard", "mouse", "touch", "gamepad"].includes(i)),
    noPrePlay: params.get("clean") === "1",
    q: params.get("q") ?? "",
  };
}

export function filterToQuery(f: Filter): string {
  const p = new URLSearchParams();
  if (f.kind) p.set("kind", f.kind);
  if (f.genre) p.set("genre", f.genre);
  if (f.multiplayer) p.set("multiplayer", "1");
  if (f.input?.length) p.set("input", f.input.join(","));
  if (f.noPrePlay) p.set("clean", "1");
  if (f.q?.trim()) p.set("q", f.q.trim());
  const s = p.toString();
  return s ? `?${s}` : "";
}

/** Cover art the launcher bundles (launcher/assets/featured), by the catalogue's `art` key. */
export function artUrl(art: string | null, variant: "full" | "thumb" | "blur" | "title" = "thumb"): string | null {
  if (!art || !/^[a-z0-9-]+$/.test(art)) return null;
  return `/art/${art}${variant === "full" ? "" : `-${variant}`}${variant === "title" ? ".png" : ".jpg"}`;
}

export function gameById(cat: Catalogue, id: string): Game | undefined {
  return cat.games.find((g) => g.id === id);
}

// ---- coming soon (waitlists, accounts wave 6) ----------------------------------------------------------------------

/** A coming-soon game has no Play anywhere on the site: it offers "Notify me in Threedom" instead. */
export function isComingSoon(g: Pick<Game, "status">): boolean {
  return g.status === "coming_soon";
}

/**
 * The expected date of a coming-soon game in the viewer's zone, or null when there's none to show: no date, a
 * malformed one, or one that has passed while the game still isn't out (then it's just "Coming soon").
 * `releaseAt` is unix seconds. `withTime` adds the time and the zone's short name (the game page).
 */
export function expectedDate(releaseAt: number | null | undefined, opts: { now?: number; timeZone?: string; locale?: string; withTime?: boolean } = {}): string | null {
  if (typeof releaseAt !== "number" || !Number.isFinite(releaseAt) || releaseAt <= 0) return null;
  const ms = releaseAt * 1000;
  if (ms <= (opts.now ?? Date.now())) return null;
  const f: Intl.DateTimeFormatOptions = { day: "numeric", month: "short", year: "numeric", timeZone: opts.timeZone };
  if (opts.withTime) Object.assign(f, { weekday: "short", hour: "numeric", minute: "2-digit", timeZoneName: "short" });
  return new Intl.DateTimeFormat(opts.locale, f).format(ms);
}

/** "Coming soon" or "Coming 18 Oct 2026". */
export function comingSoonText(g: Pick<Game, "status" | "releaseAt">, opts: Parameters<typeof expectedDate>[1] = {}): string | null {
  if (!isComingSoon(g)) return null;
  const d = expectedDate(g.releaseAt, opts);
  return d ? `Coming ${d}` : "Coming soon";
}
